import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Heart, MapPin, Menu, Search, ShoppingBag, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import Logo from "./Logo";
import AnnouncementBar from "./AnnouncementBar";
import StoreSwitcher from "./StoreSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const JEWELLERY_CATS = ["Rings", "Earrings", "Pendants", "Necklaces", "Bangles", "Mangalsutra", "Anklets"];
const COIN_FAMILIES = [
  ["Coins", "coin"], ["Jumbo", "jumbo"], ["Flip Coins", "flip"], ["Notes", "note"],
  ["Sets", "combo"], ["Belpatra", "belpatra"], ["Front & Back", "two-side"], ["Shank", "shank"],
  ["Savva Rupaya", "savva-rupaya"],
];

function PincodePicker() {
  const { pincode, setPincode } = useStore();
  const [val, setVal] = useState(pincode);
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="hidden sm:flex items-center gap-1.5 text-ivory/80 hover:text-gold text-xs transition-colors" data-testid="pincode-trigger">
          <MapPin size={14} className="text-gold" />
          <span className="font-mono" data-testid="pincode-label">{pincode || "Pincode"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" data-testid="pincode-popover">
        <p className="text-xs font-medium mb-2">Delivery pincode</p>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (/^\d{6}$/.test(val)) { setPincode(val); setOpen(false); } }}>
          <Input value={val} onChange={(e) => setVal(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="400001" className="h-9 font-mono" data-testid="pincode-input" />
          <Button type="submit" size="sm" className="h-9 bg-brand text-ivory rounded-full px-4" data-testid="pincode-set-btn">Set</Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count, setDrawerOpen } = useCart();
  const { store, storeDef, setSearchOpen, setWishlistOpen } = useStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const catLinks = store === "coins"
    ? COIN_FAMILIES.map(([label, fam]) => ({ label, to: `/shop?product_type=coin&family=${fam}` }))
    : JEWELLERY_CATS.map((c) => ({ label: c, to: `/shop?metal=${store}&category=${encodeURIComponent(c)}` }));

  const navLinkCls = "text-sm text-ink hover:text-brand transition-colors whitespace-nowrap hover:underline underline-offset-4 decoration-gold";

  return (
    <header className="sticky top-0 z-40 shadow-[0_1px_0_rgba(201,162,39,0.25)]" data-testid="navbar">
      <AnnouncementBar />

      {/* Main header — aubergine */}
      <div className="bg-brand text-ivory">
        <div className="container-x flex items-center gap-4 py-3">
          <button className="lg:hidden text-ivory" onClick={() => setMobileOpen(true)} aria-label="Menu" data-testid="mobile-menu-btn">
            <Menu size={22} />
          </button>
          <Link to="/" className="shrink-0" aria-label="Siyaara home"><Logo on="dark" /></Link>

          <div className="flex-1" />
          <PincodePicker />
          <button onClick={() => setSearchOpen(true)} className="text-ivory/80 hover:text-gold transition-colors" aria-label="Search" data-testid="nav-search-btn">
            <Search size={19} />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-ivory/80 hover:text-gold transition-colors" aria-label="Account" data-testid="nav-account-btn">
                <User size={19} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {user ? (
                <>
                  <DropdownMenuItem onClick={() => navigate("/account")} data-testid="nav-my-account">My Account</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/account")} data-testid="nav-my-orders">Orders</DropdownMenuItem>
                  {["admin", "owner", "manager", "staff"].includes(user.role) && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin">Admin</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout">Sign out</DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem onClick={() => navigate("/login")} data-testid="nav-login">Sign in</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <button onClick={() => setWishlistOpen(true)} className="text-ivory/80 hover:text-gold transition-colors" aria-label="Wishlist" data-testid="nav-wishlist-btn">
            <Heart size={19} />
          </button>
          <button onClick={() => setDrawerOpen(true)} className="relative text-ivory/80 hover:text-gold transition-colors" aria-label="Cart" data-testid="nav-cart-btn">
            <ShoppingBag size={19} />
            {count > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-gold text-brand text-[10px] font-bold rounded-full h-4 min-w-4 px-0.5 flex items-center justify-center" data-testid="nav-cart-count">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Category nav — ivory */}
      <nav className="hidden lg:block bg-ivory" data-testid="main-nav">
        <div className="container-x flex items-center gap-6 py-2 overflow-x-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 text-sm font-medium text-brand" data-testid="nav-shop-menu">
                Shop {storeDef.label} <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[34rem] p-5 grid grid-cols-3 gap-5" data-testid="nav-mega-menu">
              <div>
                <p className="eyebrow mb-2">CATEGORY</p>
                {catLinks.slice(0, 7).map((l) => (
                  <DropdownMenuItem key={l.label} asChild><Link to={l.to} className="text-sm">{l.label}</Link></DropdownMenuItem>
                ))}
              </div>
              <div>
                <p className="eyebrow mb-2">BY PRICE</p>
                {[["Under ₹10,000", "/gifts/under-10000"], ["Under ₹15,000", "/gifts/under-15000"], ["Under ₹20,000", "/gifts/under-20000"], ["Premium", "/gifts/premium"]].map(([label, to]) => (
                  <DropdownMenuItem key={label} asChild><Link to={to} className="text-sm">{label}</Link></DropdownMenuItem>
                ))}
              </div>
              <div>
                <p className="eyebrow mb-2">EXPLORE</p>
                <DropdownMenuItem asChild><Link to="/shop?personalised=true" className="text-sm">Personalised</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/gifts" className="text-sm">Shop by Bond</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/pia" className="text-sm">The Pia Edit</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/gold-rate" className="text-sm">Today's Rates</Link></DropdownMenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          {catLinks.slice(0, 6).map((l) => (
            <Link key={l.label} to={l.to} className={navLinkCls} data-testid={`nav-cat-${l.label.toLowerCase().replace(/[\s&]+/g, "-")}`}>{l.label}</Link>
          ))}
          <Link to="/gifts" className={`${navLinkCls} text-brand-3`} data-testid="nav-gifts">Shop by Bond</Link>
          <Link to="/verify" className={navLinkCls} data-testid="nav-verify">Verify Certificate</Link>
          <Link to="/shop?personalised=true" className={`${navLinkCls} text-brand-3`} data-testid="nav-personalised">Personalised</Link>
          <div className="flex-1" />
          <Link to="/track-order" className={`${navLinkCls} text-ink-2`} data-testid="nav-track-order">Track Order</Link>
        </div>
      </nav>

      <StoreSwitcher />

      {/* Mobile menu */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-80 bg-ivory" data-testid="mobile-menu">
          <SheetHeader><SheetTitle><Logo on="light" /></SheetTitle></SheetHeader>
          <div className="mt-6 flex flex-col gap-1">
            {[{ label: `All ${storeDef.label}`, to: storeDef.path === "/coins" ? "/shop?product_type=coin" : `/shop?metal=${store}` },
              ...catLinks,
              { label: "Personalised", to: "/shop?personalised=true" },
              { label: "Shop by Bond", to: "/gifts" },
              { label: "Verify Certificate", to: "/verify" },
              { label: "The Pia Edit", to: "/pia" },
              { label: "Track Order", to: "/track-order" },
              { label: "Today's Gold Rate", to: "/gold-rate" },
              { label: "FAQs", to: "/faq" },
            ].map((l) => (
              <Link key={l.label} to={l.to} onClick={() => setMobileOpen(false)}
                className="py-2.5 border-b border-brand/10 text-sm text-ink hover:text-brand transition-colors"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[\s&']+/g, "-")}`}>
                {l.label}
              </Link>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}
